import type { ContentStatus, Channel, ChannelPublicationTime } from '@/services/business/calendar';

export type PublicationItem =
  | { type: 'regular'; date: Date; time: string; data: ChannelPublicationTime[] }
  | { type: 'ab'; date: Date; time: string; data: ChannelPublicationTime };

export interface ContentEvent {
  id: string;
  date: Date;
  type: 'content' | 'ab_testing';
  title: string;
  description?: string;
  contentStatus?: ContentStatus;
  channels?: Channel[];
  publicationTimes?: ChannelPublicationTime[];
  videoUrl?: string;
  coverUrl?: string;
  video2Url?: string;
  video2Thumbnail?: string;
  video3Url?: string;
  video3Thumbnail?: string;
  isMultihook?: boolean;
  isBanger?: boolean;
  isDraft?: boolean;
  instagramCrosspostDisabled?: boolean;
  versionAEnabled?: boolean;
  versionBEnabled?: boolean;
  versionCEnabled?: boolean;
  linkedContentId?: string;
  user_id?: string;
}

export interface SocialConnection {
  id: string;
  user_id: string;
  platform: string;
  connected: boolean;
  username: string | null;
  created_at: string;
  updated_at: string;
}

export const groupPublicationsByTime = (publications: ChannelPublicationTime[], excludeYoutube: boolean = false) => {
  const grouped = new Map<string, ChannelPublicationTime[]>();
  const filteredPubs = excludeYoutube ? publications.filter(p => p.channel !== 'youtube') : publications;
  filteredPubs.forEach(pub => {
    const key = `${pub.date.toDateString()}_${pub.time}`;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(pub);
  });
  return Array.from(grouped.values());
};

// Validates multihook publication order: YouTube (AB test) must be BEFORE all other platforms.
// Returns the first offending non-YouTube publication, or null if the order is valid.
export const validateMultihookOrder = (
  youtubeDatetime: Date,
  nonYoutubePublications: ChannelPublicationTime[]
): { offendingChannel: Channel } | null => {
  for (const pub of nonYoutubePublications) {
    const pubDatetime = new Date(pub.date);
    const [h, m] = pub.time.split(':').map(Number);
    pubDatetime.setHours(h, m, 0, 0);
    if (youtubeDatetime >= pubDatetime) {
      return { offendingChannel: pub.channel };
    }
  }
  return null;
};

// Compute the AB Testing display date for a multihook — 1 calendar day before the YouTube publication.
// Mirrors the calendar store's createAbTestingEvent logic exactly.
export const getAbTestingDate = (publications: ChannelPublicationTime[]): ChannelPublicationTime | null => {
  const youtubePub = publications.find(p => p.channel === 'youtube');
  if (!youtubePub) return null;

  const abDate = new Date(youtubePub.date);
  const [h, m] = youtubePub.time.split(':').map(Number);
  abDate.setHours(h, m, 0, 0);
  abDate.setDate(abDate.getDate() - 1); // 1 calendar day before, not 24h (avoids DST drift)

  return {
    channel: 'youtube' as Channel,
    date: abDate,
    time: `${String(abDate.getHours()).padStart(2, '0')}:${String(abDate.getMinutes()).padStart(2, '0')}`
  };
};
